import { ColumnFlexCentered, FlexCentered } from '~/components/common/containers'
import React, { useEffect, useState } from 'react'
import {
  cancelAccount,
  fetchInvoices,
  pollForSubscriptionStatus
} from '../services/api'

import BillingDetailsDialog from './billing-details-dialog'
import Button from '~/components/common/button'
import CancelAccountDialog from './cancel-account-dialog'
import EditPaymentInfoDialog from './edit-payment-info-dialog'
import LogoutButton from './cancel-button'
import { UNPAID_INVOICES } from '~/core/constants'
import appConfig from '../app-config'
import commonCss from '~/core/css'
import componentsConfig from '../components-config'
import { getPriceString } from '../core/utils'
import { showErrorNotification } from '~/core/ui'
import styled from 'styled-components'
import { translate } from '~/core/translate'
import withNotifications from '~/components/with-notifications'
import withStripeElements from './with-stripe-elements'

const StyledFlexCentered = styled(FlexCentered)`
  justify-content: center;
`

const MaxWidthContainer = styled(ColumnFlexCentered)`
  max-width: 550px;
`

const Subtitle = styled.div`
  font-weight: 600;
  font-size: 36px;
  line-height: 48px;
  margin-bottom: 24px;
`

const Description = styled.div`
  font-size: 15px;
  line-height: 24px;
  text-align: center;
  color: #000000;
  margin-bottom: 24px;
`

const FixWidthButton = styled(Button)`
  width: 240px;
  margin-bottom: 24px;
`

const CancelSubscriptionbutton = styled(FixWidthButton)`
  color: #E12D2D;
`

const SectionDelimiter = styled(FlexCentered).attrs({
  fullWidth: true
})`
  margin-bottom: 24px;
  padding: 12px 0;
  max-width: 240px;
  color: #5E6D7A;
  span {
    text-transform: uppercase;
    margin: 0 11px;
    font-weight: 600;
    font-size: 13px;
    line-height: 20px;
  }
`

const Underline = styled.hr`
  ${commonCss.resetBoxProps};
  border-bottom: 1px solid #D1DBE8;
  width: 100%;
`

const InvoicesSummary = styled(FlexCentered)`
  padding: 12px 16px;
  width: calc(100% - 24px);
  margin-bottom: 24px;
  justify-content: space-between;
  background: #F0F3F7;
`

const InvoiceSummaryLabel = styled.div`
  color: #000;
`

const ViewBillingDetailsLink = styled.div`
  font-size: 13px;
  line-height: 20px;
  color: #0376DA;
  cursor: pointer;
`

const FooterContainer = styled(FlexCentered)`
  position: fixed;
  left: 0;
  bottom: 0;
  justify-content: flex-start;
  padding: 18px 24px;
  font-size: 11px;
  color: #5E6D7A;
  width: calc(100% - 48px);
  box-shadow: 0px -1px 0px #F0F3F7;
  background:  #FFF;
`

const LightLabel = styled.div`
  font-size: 11px;
  color: #5E6D7A;
  margin-right: 30px;
`

const LightLabelLink = styled.a.attrs({
  target: '_blank'
})`
  font-size: 11px;
  color: #5E6D7A;
  margin-right: 30px;
  text-decoration: none;
`

const AppLabelFooter = styled.div`
  font-weight: bold;
  font-size: 13px;
  color: #5E6D7A;
  margin-left: auto;
`

const Spacer = styled.div`
  height: 100px;
`

const SubscriptionExpiredPage = ({ userInfo }) => {
  const [isPolling, setIsPolling] = useState(false)
  const [dialog, setDialog] = useState(null)
  const [invoices, setInvoices] = useState([])
  const unpaidInvoices = invoices.filter(inv => UNPAID_INVOICES.includes(inv.status))
  const amount = unpaidInvoices.reduce((acc, currentInv) => acc + currentInv.amount / 100, 0)
  const currency = unpaidInvoices && unpaidInvoices.length ? unpaidInvoices[0].currency : 'usd'
  const closeDialog = () => setDialog(null)

  const { Header } = componentsConfig.current

  const onPaymentInfoUpdated = async () => {
    closeDialog()
    setIsPolling(true)

    try {
      await pollForSubscriptionStatus()
      appConfig.current.goToApp()
    } catch {
      showErrorNotification(translate('payment_failed'))
    }

    setIsPolling(false)
  }

  const cancelSubscription = async () => {
    closeDialog()
    try {
      await cancelAccount()
    } catch {
      showErrorNotification(translate('could_not_delete_account'))
    }
  }

  const openCancelAccountDialog = () => setDialog(
    <CancelAccountDialog
      onConfirm={cancelSubscription}
      onClose={closeDialog}
    />
  )

  const openPaymentDialog = () => setDialog(
    <EditPaymentInfoDialog
      userInfo={userInfo}
      onClose={closeDialog}
      onSuccess={onPaymentInfoUpdated}
    />)

  const openBillingDetails = () => setDialog(
    <BillingDetailsDialog
      invoices={unpaidInvoices}
      userInfo={userInfo}
      onClose={closeDialog}
    />)

  useEffect(() => {
    const getBills = async () => {
      try {
        const invoices = await fetchInvoices()
        setInvoices(invoices)
      } catch {
        showErrorNotification(translate('failed_getting_invoices'))
      }
    }

    getBills()
  }, [])

  return (
    <>
      <StyledFlexCentered>
        <MaxWidthContainer>
          <Header />
          {isPolling && (
            <>
              <Subtitle>{translate('processing_payment')}</Subtitle>
              <Description
                dangerouslySetInnerHTML={{ __html: translate('processing_payment_info_updated', { email: userInfo.email }) }}
              />
            </>
          )}
          {!isPolling && (
            <>

              <Subtitle>{translate('account_suspended')}</Subtitle>
              <Description
                dangerouslySetInnerHTML={{
                  __html: translate('account_suspended_description', { email: userInfo.email })
                }}
              />
              {amount !== 0 && (
                <InvoicesSummary>
                  <InvoiceSummaryLabel
                    dangerouslySetInnerHTML={{
                      __html: translate('invoice_summary', { count: unpaidInvoices.length, amount: getPriceString(currency, amount) })
                    }}
                  />
                  <ViewBillingDetailsLink onClick={openBillingDetails}>{translate('bill_details')}</ViewBillingDetailsLink>
                </InvoicesSummary>
              )}
              <FixWidthButton onClick={openPaymentDialog}>{translate('update_payment_details')}</FixWidthButton>
              <SectionDelimiter>
                <Underline />
                <span>{translate('or')}</span>
                <Underline />
              </SectionDelimiter>
              <CancelSubscriptionbutton
                secondary
                onClick={openCancelAccountDialog}
              >
                {translate('cancel_subscription')}
              </CancelSubscriptionbutton>
              <LogoutButton />
              <Spacer />
            </>
          )}
          {dialog}
        </MaxWidthContainer>
      </StyledFlexCentered>
      <FooterContainer>
        <LightLabel>{translate('all_rights_reserved')}</LightLabel>
        <LightLabelLink href={appConfig.current.privacyPolicyUrl}>{translate('privacy_policy')}</LightLabelLink>
        <LightLabelLink href={appConfig.current.cookiesPolicyUrl}>{translate('cookies')}</LightLabelLink>
        <LightLabelLink href={appConfig.current.termsAndConditionsUrl}>{translate('terms_and_conditions')}</LightLabelLink>
        <AppLabelFooter>{appConfig.current.fullAppName()}</AppLabelFooter>
      </FooterContainer>
    </>
  )
}

export default withStripeElements(withNotifications(SubscriptionExpiredPage))
